import React, { useEffect, useState } from "react";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  Tag,
  TimePicker,
  message,
  Select,
  Radio,
} from "antd";
import MyModal from "./CustomModal/Modal";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getUserCalendarDetails, getUserListThunk } from "../slices/userSlice";
import { addCalenderDetails } from "../services/awsServices";
import toastMessage from "../utils/toastMessage";
import { LinkIcon, ListIcon } from "../utils/icons";

const filter = [
  // {
  //   title: "View All",
  //   color: "#8F8F8F",
  // },
  {
    title: "Meetings",
    color: "#FF6B38",
  },
  {
    title: "Challenges",
    color: "#9945C2",
  },
  {
    title: "Courses",
    color: "#FF3D4A",
  },
  {
    title: "Tasks",
    color: "#52B070",
  },
];

function AddEventsModal({
  isModalVisible,
  handleCancel,
  handleOk,
  formData,
  setFormData,
}) {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const userList = useSelector((state) => state.user.userList) || [];
  const { VITE_APP_DATE_FORMAT } = import.meta.env;

  useEffect(() => {
    dispatch(getUserListThunk());
  }, []);
  const options = userList.map((user) => ({
    label: user.Name.S,
    value: user.PK.S,
  }));

  // This function is triggered when the "Save" button is clicked.
  const onSave = () => {
    // Check if the "Event Title" is filled out.
    if (!formData.Title) {
      // If not, display an error message and prevent form submission.
      toastMessage("Event title is required", "error");
      setSubmitAttempted(true); // Indicate a submit attempt was made to trigger error visuals if needed.
      return; // Prevent further execution.
    }
    if (!formData.EventDate) {
      // If not, display an error message and prevent form submission.
      toastMessage("Date is required", "error");
      setSubmitAttempted(true); // Indicate a submit attempt was made to trigger error visuals if needed.
      return; // Prevent further execution.
    }
    if (!formData.StartTime) {
      // If not, display an error message and prevent form submission.
      toastMessage("Start Time is required", "error");
      setSubmitAttempted(true); // Indicate a submit attempt was made to trigger error visuals if needed.
      return; // Prevent further execution.
    }
    if (!formData.EndTime) {
      // If not, display an error message and prevent form submission.
      toastMessage("EndTime Time is required", "error");
      setSubmitAttempted(true); // Indicate a submit attempt was made to trigger error visuals if needed.
      return; // Prevent further execution.
    }
    // If the "Event Title" is filled out, proceed with the original submission logic.
    // Consider passing formData here if handleOk needs it.
    setLoader(true);

    const payload = {
      ...formData,
      EventDate: dayjs(formData.EventDate).format(VITE_APP_DATE_FORMAT),
    };
    addCalenderDetails(payload)
      .then((res) => {
        console.log(res, " = res:updateBasicInfo");
        // message.success(
        //   res.data?.message || "Calendar event added successfully",
        //   "success"
        // );
        dispatch(getUserCalendarDetails()).unwrap();
        toastMessage(
          res.data?.message || "Calendar event added successfully",
          "success"
        );
        setLoader(false);
        handleOk();
      })
      .catch((error) => {
        console.log(error, " = error:updateBasicInfo");
        toastMessage("Something went wrong", "error");
      });
  };
  return (
    <MyModal
      className="addEventStyle"
      width={"400px"}
      footer={false}
      title="Add Event"
      open={isModalVisible}
      onOk={onSave}
      onCancel={() => {
        setFormData({});
        handleCancel();
      }}
      maskClosable={false}
    >
      <div className="space-y-5">
        <Input
          name="newEventTitle"
          placeholder="Event Title"
          value={formData.Title}
          onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
          status={submitAttempted && !formData.Title ? "error" : undefined}
        />
        {/* Other input fields and components remain the same */}
        <DatePicker
          disabledDate={(current) => {
            return current && current < dayjs().startOf("day");
          }}
          className="w-full"
          onChange={(date, dateString) =>
            setFormData({ ...formData, EventDate: date })
          }
          value={formData.EventDate}
        />
        <TimePicker.RangePicker
          format={"HH:mm"}
          showSecond={false}
          className="w-full"
          order={false}
          onChange={(time, timeString) => {
            if (timeString[0] === timeString[1]) {
              toastMessage("Start time and End time can't be same", "error");
              return;
            }
            // Assuming 'time' is the array of Day.js objects you receive
            if (time.length === 2) {
              // Update the formData state with the start and end times
              setFormData((prevFormData) => ({
                ...prevFormData,
                StartTime: time[0].toDate(), // Converts Day.js object to JavaScript Date object
                EndTime: time[1].toDate(), // Converts Day.js object to JavaScript Date object
              }));
            }
          }}
          // disabled={(time, timeString) => timeString[0] === timeString[1]}
          value={
            formData.StartTime && formData.EndTime
              ? [dayjs(formData.StartTime), dayjs(formData.EndTime)] // Ensure these are Day.js objects for the value prop
              : []
          }
        />
        <Select
          showSearch
          className="w-full"
          mode="multiple"
          allowClear
          value={formData.Users}
          placeholder="Please select user"
          onChange={(value) => setFormData({ ...formData, Users: value })}
          options={options}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        />
        <Input
          prefix={<LinkIcon />}
          name="meeting"
          placeholder="Meeting Link"
          value={formData.MeetingLink}
          onChange={(e) =>
            setFormData({ ...formData, MeetingLink: e.target.value })
          }
        />

        <Radio.Group className="space-x-1" value={formData.EventType}>
          {filter.map((tag, i) => (
            <ConfigProvider
              key={i}
              theme={{
                components: {
                  Radio: {
                    colorPrimary: "white",
                    colorPrimaryHover: "white",
                    buttonCheckedBg: tag.color,
                  },
                },
              }}
            >
              <Radio.Button
                className="border-none rounded-3xl"
                value={tag.title}
                // key={i}
                onChange={(e) =>
                  setFormData({ ...formData, EventType: e.target.value })
                }
              >
                {tag.title}
              </Radio.Button>
            </ConfigProvider>
          ))}
        </Radio.Group>
        <Input
          prefix={<ListIcon />}
          name="description"
          placeholder="Add Description"
          value={formData.Description}
          onChange={(e) =>
            setFormData({ ...formData, Description: e.target.value })
          }
        />
        <div className="flex justify-center gap-5">
          <Button
            onClick={() => {
              setFormData({});
              handleCancel();
            }}
            className="text-orange2 rounded-3xl border-orange2 w-[100px]"
          >
            Cancel
          </Button>
          <Button
            loading={loader}
            onClick={onSave}
            className="text-pureWhite rounded-3xl bg-sandAgainBtn border-none w-[100px]"
          >
            Save
          </Button>
        </div>
      </div>
    </MyModal>
  );
}

export default AddEventsModal;
